import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { api } from "../lib/api";

/**
 * Typeahead search for NSE tickers / company names. Hits /api/stocks/search.
 * Props:
 *   - value: current input string
 *   - onChange: (value) => void   — controlled input
 *   - onPick:  (match) => void    — invoked when user selects a suggestion. Match = {symbol, name, isin, ...}
 *   - placeholder
 *   - dataTestidBase (default: "ticker-search")
 */
export default function TickerSearchInput({
  value,
  onChange,
  onPick,
  placeholder = "Type a company or ticker (e.g. HDFC, INFY, Tata Steel)",
  dataTestidBase = "ticker-search",
  disabled = false,
  className = "",
}) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!value || value.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/stocks/search", { params: { q: value.trim() } });
        setResults(data.results || []);
        setHighlight(0);
        setOpen((data.results || []).length > 0);
      } catch {
        setResults([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const pickMatch = (m) => {
    if (!m) return;
    setOpen(false);
    setResults([]);
    if (onPick) onPick(m);
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      pickMatch(results[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={"relative " + className}>
      <div className="relative">
        <Search className="w-4 h-4 text-stone_brand-light absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="h-10 w-full pl-9 pr-8 text-sm rounded-sm border border-line bg-white focus:outline-none focus:ring-1 focus:ring-moss disabled:opacity-50"
          data-testid={`${dataTestidBase}-input`}
          autoComplete="off"
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-stone_brand-light hover:text-terracotta p-1"
            data-testid={`${dataTestidBase}-clear`}
            aria-label="Clear"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <div
          className="absolute z-40 mt-1 w-full bg-white border border-line rounded-sm shadow-lg overflow-hidden"
          data-testid={`${dataTestidBase}-dropdown`}
        >
          <div className="max-h-72 overflow-y-auto">
            {results.map((r, i) => (
              <button
                key={`${r.symbol}-${r.isin || i}`}
                type="button"
                onClick={() => pickMatch(r)}
                onMouseEnter={() => setHighlight(i)}
                className={
                  "w-full text-left px-3 py-2 flex items-center justify-between gap-3 border-b border-line last:border-b-0 " +
                  (i === highlight ? "bg-moss-bg/50" : "bg-white hover:bg-bone-50")
                }
                data-testid={`${dataTestidBase}-result-${i}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-sm font-semibold text-stone_brand-dark truncate">
                    {r.symbol}
                  </div>
                  <div className="text-[0.7rem] text-stone_brand truncate">{r.name}</div>
                </div>
                {r.industry && (
                  <div className="text-[0.6rem] font-mono uppercase tracking-widest text-stone_brand-light flex-shrink-0">
                    {r.industry}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      {open && loading && results.length === 0 && (
        <div className="absolute z-40 mt-1 w-full bg-white border border-line rounded-sm px-3 py-2 text-xs text-stone_brand-light">
          Searching…
        </div>
      )}
    </div>
  );
}
